<script setup>
// SiteUp: "Campos da IA" — regras de preenchimento de campos personalizados por
// agente. O atendente escolhe um campo personalizado JÁ EXISTENTE (select que
// puxa as CustomAttributeDefinition da conta) e escreve QUANDO a IA deve
// preenchê-lo. Salva em config.custom_field_rules = [{ key, when }]. Só os campos
// listados aqui entram no prompt da IA (controle 100% humano).
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

const route = useRoute();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const conversationAttributes = useMapGetter('attributes/getConversationAttributes');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isSaving = computed(() => uiFlags.value.updatingItem);

const rows = ref([]);

const fieldOptions = computed(() => {
  const conv = (conversationAttributes.value || []).map(a => ({
    key: a.attributeKey,
    label: `[conversa] ${a.attributeDisplayName}`,
  }));
  const contact = (contactAttributes.value || []).map(a => ({
    key: a.attributeKey,
    label: `[contato] ${a.attributeDisplayName}`,
  }));
  return [...conv, ...contact];
});

const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
}));

const initRows = () => {
  const rules = assistant.value?.config?.custom_field_rules || [];
  rows.value = rules.map(r => ({ key: r.key, when: r.when }));
};

const addRow = () => {
  rows.value.push({ key: '', when: '' });
};

const removeRow = index => {
  rows.value.splice(index, 1);
};

const save = async () => {
  const clean = rows.value
    .filter(r => r.key && r.when && r.when.trim())
    .map(r => ({ key: r.key, when: r.when.trim() }));
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      config: { custom_field_rules: clean },
    });
    useAlert('Regras de campos atualizadas.');
  } catch {
    useAlert('Não foi possível salvar.');
  }
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('attributes/get').catch(() => {}),
    store.dispatch('captainAssistants/show', assistantId.value).catch(() => {}),
  ]);
  initRows();
});

watch(assistant, () => {
  if (rows.value.length === 0) initRows();
});
</script>

<template>
  <PageLayout
    header-title="Campos da IA"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        heading="Campos da IA"
        description="Escolha um campo personalizado e diga QUANDO a IA deve preenchê-lo durante a conversa. Só os campos listados aqui são preenchidos automaticamente (a IA usa a chave exata e salva na ficha)."
      />

      <div class="flex flex-col gap-3 mt-7">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="flex items-start gap-2"
        >
          <select
            v-model="row.key"
            class="w-60 shrink-0 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          >
            <option value="">Selecione um campo…</option>
            <option v-for="opt in fieldOptions" :key="opt.key" :value="opt.key">
              {{ opt.label }}
            </option>
          </select>
          <input
            v-model="row.when"
            type="text"
            placeholder="Quando preencher (ex.: quando o cliente informar o valor da conta de luz)"
            class="flex-1 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
          />
          <Button
            icon="i-ri-delete-bin-line"
            slate
            ghost
            sm
            class="shrink-0 mt-0.5"
            @click="removeRow(index)"
          />
        </div>

        <div v-if="rows.length === 0" class="text-sm text-n-slate-11">
          Nenhuma regra ainda. Clique em "Adicionar campo" para começar.
        </div>

        <div>
          <Button
            label="Adicionar campo"
            icon="i-ri-add-line"
            slate
            faded
            sm
            @click="addRow"
          />
        </div>
      </div>

      <div class="mt-6">
        <Button label="Salvar regras" :is-loading="isSaving" @click="save" />
      </div>
    </template>
  </PageLayout>
</template>
